import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ApiCrudSection, {
  type Column,
  type FormField,
  type Row,
} from "../components/ApiCrudSection";
import {
  fetchAllAttendances,
  fetchAttendanceSummary,
  createAttendance,
  updateAttendance,
  deleteAttendance,
} from "../services/payrollService";
import { fetchTeamMembers } from "../../team/services/teamService";
import {
  ATTENDANCE_STATUS_OPTIONS,
  attendanceStatusLabel,
  ATTENDANCE_STATUS_TONES,
  buildThemeClasses,
  clockSourceLabel,
  formatDays,
} from "../theme";
import type { AttendanceStatus, AttendanceRecord, AttendanceSummary } from "../types";
import { useIsStaffAdmin } from "../../../hooks/useIsStaffAdmin";
import { useTheme } from "../../../context/ThemeContext";
import AttendanceHeatmap from "../../../components/staff-ops/AttendanceHeatmap";
import {
  currentHalf,
  localToday,
  periodRange,
  type Half,
  type PeriodMode,
} from "../attendanceRange";

// ---------------------------------------------------------------------------
// Registro de asistencia diaria por colaborador (AgroCloud#36).
//
// Es la base de la liquidación por día (AgroCloud#38): de aquí salen las jornadas
// trabajadas del periodo. Por eso el backend rechaza dos registros del mismo
// colaborador en la misma fecha — pagaría el día dos veces.
//
// Las horas trabajadas se derivan de los marcajes en el backend (y cruzan
// medianoche, para el turno nocturno de riego). Si falta un marcaje se muestran
// como "—", no como 0: "no marcó salida" no es "trabajó cero horas".
//
// La columna "Fuente" siempre dirá "Manual" mientras AgroCloud#83 (biometría/reloj
// control) sea una maqueta: el backend rechaza con 501 cualquier otra fuente.
//
// Sobre el shell CRUD reutilizable (tabla + tarjetas + modal), con toggle
// grid/lista, responsive y dark mode.
// ---------------------------------------------------------------------------

/** Número del formulario, o `null` si no se informó (≠ 0). */
function numOrNull(raw: string | undefined): number | null {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** Traduce los valores del formulario al payload del backend. */
function toPayload(values: Record<string, string>): Partial<AttendanceRecord> {
  return {
    date: values.date,
    teamMemberId: values.teamMemberId,
    teamMemberName: values.teamMemberName || null,
    status: (values.status || "present") as AttendanceStatus,
    checkIn: values.checkIn || null,
    checkOut: values.checkOut || null,
    overtime: numOrNull(values.overtime),
    days: numOrNull(values.days),
    locationId: values.locationId || null,
    locationName: values.locationName || null,
    projectId: values.projectId || null,
    costCenterId: values.costCenterId || null,
    // AgroCloud#83 sigue siendo maqueta: desde la consola solo hay marcaje manual.
    source: "manual",
    notes: values.notes || null,
  };
}

type AttendanceView = "calendar" | "table";
/** Llave de la vista elegida en Asistencia (#1595). */
export const ATTENDANCE_VIEW_KEY = "payroll.attendance.view";
function readAttendanceView(): AttendanceView {
  try {
    return window.localStorage.getItem(ATTENDANCE_VIEW_KEY) === "table" ? "table" : "calendar";
  } catch {
    return "calendar";
  }
}

interface PersonOption {
  id: string;
  name: string;
  /** Color del avatar de la ficha (#1595), si lo trae. */
  color?: string | null;
}

/** Totales del rango sumando el consolidado por persona del servidor. */
function totalsOf(summary: AttendanceSummary[]) {
  const round = (n: number) => Math.round(n * 100) / 100;
  return summary.reduce(
    (acc, s) => ({
      records: acc.records + (Number(s.records) || 0),
      daysWorked: round(acc.daysWorked + (Number(s.daysWorked) || 0)),
      absences: acc.absences + (Number(s.absences) || 0),
      hoursWorked: round(acc.hoursWorked + (Number(s.hoursWorked) || 0)),
      overtime: round(acc.overtime + (Number(s.overtime) || 0)),
    }),
    { records: 0, daysWorked: 0, absences: 0, hoursWorked: 0, overtime: 0 },
  );
}

export default function AttendanceSection() {
  // #1007: solo quien administra modifica; los demás ven lo suyo en solo lectura.
  // #1143: y solo quien administra (los dueños de Staff) elige a la persona. El
  // servidor acota igual: esto solo decide qué se pinta.
  const isStaffAdmin = useIsStaffAdmin("payroll");
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const tc = buildThemeClasses(isDarkMode);
  const [rows, setRows] = useState<AttendanceRecord[]>([]);
  const [summary, setSummary] = useState<AttendanceSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // --- Filtros (#1143) — por omisión, la quincena en curso ---
  const [mode, setMode] = useState<PeriodMode>("fortnight");
  const [month, setMonth] = useState(() => localToday().slice(0, 7));
  const [half, setHalf] = useState<Half>(() => currentHalf());
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [teamMemberId, setTeamMemberId] = useState("");
  const [people, setPeople] = useState<PersonOption[]>([]);
  // #1595: vista de calendario (persona × día) del mismo rango, encima de la
  // tabla. La elección se recuerda en este navegador (comodidad, no dato).
  const [view, setViewState] = useState<AttendanceView>(readAttendanceView);
  const setView = (v: AttendanceView) => {
    setViewState(v);
    try {
      window.localStorage.setItem(ATTENDANCE_VIEW_KEY, v);
    } catch {
      /* sin almacenamiento: la vista vale solo para esta visita */
    }
  };

  const customActive = Boolean(customFrom || customTo);
  const range = useMemo(
    () =>
      customActive
        ? { from: customFrom, to: customTo }
        : periodRange(mode, month || localToday().slice(0, 7), half),
    [customActive, customFrom, customTo, mode, month, half],
  );
  const invalidRange = Boolean(range.from && range.to && range.from > range.to);
  // Un colaborador no elige persona: si quedara algo guardado, no se manda.
  const personFilter = isStaffAdmin ? teamMemberId : "";

  const load = useCallback(async () => {
    if (invalidRange) {
      setRows([]);
      setSummary(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const filters = {
      from: range.from || undefined,
      to: range.to || undefined,
      teamMemberId: personFilter || undefined,
    };
    try {
      const { items } = await fetchAllAttendances(filters);
      setRows(items);
    } catch {
      setError(t("payroll.loadError"));
    } finally {
      setLoading(false);
    }
    // El resumen es un extra: si falla, la lista sigue sirviendo.
    try {
      setSummary(await fetchAttendanceSummary(filters));
    } catch {
      setSummary(null);
    }
  }, [t, range.from, range.to, personFilter, invalidRange]);

  useEffect(() => {
    void load();
  }, [load]);

  // Personas para el selector: solo a quien administra le sirve la lista.
  useEffect(() => {
    if (!isStaffAdmin) return;
    let alive = true;
    fetchTeamMembers({ limit: 500 })
      .then(({ items }) => {
        if (!alive) return;
        setPeople(
          (items ?? [])
            .map((m) => ({
              id: m.id,
              name: [m.name, m.lastName].filter(Boolean).join(" ") || m.id,
              color: ((m as unknown as { avatarColor?: string | null }).avatarColor ?? null) as string | null,
            }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        );
      })
      .catch(() => {
        if (alive) setPeople([]);
      });
    return () => {
      alive = false;
    };
  }, [isStaffAdmin]);

  // Sin registros en el rango no hay nada que resumir (y un «0 h» confundiría).
  const totals = summary && summary.length > 0 ? totalsOf(summary) : null;
  const inputCls = `rounded-lg border px-2 py-1.5 text-sm ${tc.border} ${tc.cardBg} ${tc.text}`;
  const segBtn = (active: boolean) =>
    `px-3 py-1.5 text-sm transition-colors ${
      active ? "bg-primary-500 text-white" : `${tc.textSecondary} ${tc.buttonHover}`
    }`;

  const viewSwitch = (
    <div role="group" aria-label={t("staffOps.heatmap.view")} className={`inline-flex overflow-hidden rounded-lg border ${tc.border}`} data-testid="attendance-view">
      {(["calendar", "table"] as const).map((v) => (
        <button key={v} type="button" aria-pressed={view === v} onClick={() => setView(v)} className={segBtn(view === v)}>
          <i className={`${v === "calendar" ? "ri-calendar-2-line" : "ri-table-line"} mr-1`} aria-hidden="true" />
          {t(`staffOps.heatmap.${v}`)}
        </button>
      ))}
    </div>
  );

  const filterBar = (
    <div
      data-testid="attendance-filters"
      className={`mb-4 rounded-2xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}
    >
      <div className="flex flex-wrap items-end gap-4">
        {/* Quincena / Mes */}
        <div className="flex flex-col gap-1">
          <span className={`text-xs ${tc.textSecondary}`}>
            {t("payroll.attendanceFilters.period")}
          </span>
          <div
            role="group"
            aria-label={t("payroll.attendanceFilters.period")}
            className={`inline-flex overflow-hidden rounded-lg border ${tc.border} ${
              customActive ? "opacity-50" : ""
            }`}
          >
            <button
              type="button"
              aria-pressed={mode === "fortnight"}
              className={segBtn(mode === "fortnight")}
              onClick={() => setMode("fortnight")}
            >
              {t("payroll.attendanceFilters.fortnight")}
            </button>
            <button
              type="button"
              aria-pressed={mode === "month"}
              className={segBtn(mode === "month")}
              onClick={() => setMode("month")}
            >
              {t("payroll.attendanceFilters.month")}
            </button>
          </div>
        </div>

        <label className="flex flex-col gap-1">
          <span className={`text-xs ${tc.textSecondary}`}>
            {t("payroll.attendanceFilters.monthLabel")}
          </span>
          <input
            type="month"
            aria-label={t("payroll.attendanceFilters.monthLabel")}
            value={month}
            disabled={customActive}
            onChange={(e) => setMonth(e.target.value)}
            className={inputCls}
          />
        </label>

        {mode === "fortnight" && (
          <label className="flex flex-col gap-1">
            <span className={`text-xs ${tc.textSecondary}`}>
              {t("payroll.attendanceFilters.fortnight")}
            </span>
            <select
              aria-label={t("payroll.attendanceFilters.half")}
              value={half}
              disabled={customActive}
              onChange={(e) => setHalf(e.target.value as Half)}
              className={inputCls}
            >
              <option value="Q1">{t("payroll.attendanceFilters.firstHalf")}</option>
              <option value="Q2">{t("payroll.attendanceFilters.secondHalf")}</option>
            </select>
          </label>
        )}

        {/* Rango libre: manda sobre la quincena/mes */}
        <label className="flex flex-col gap-1">
          <span className={`text-xs ${tc.textSecondary}`}>{t("payroll.attendanceFilters.from")}</span>
          <input
            type="date"
            aria-label={t("payroll.attendanceFilters.from")}
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className={`text-xs ${tc.textSecondary}`}>{t("payroll.attendanceFilters.to")}</span>
          <input
            type="date"
            aria-label={t("payroll.attendanceFilters.to")}
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            className={inputCls}
          />
        </label>
        {customActive && (
          <button
            type="button"
            onClick={() => {
              setCustomFrom("");
              setCustomTo("");
            }}
            className={`rounded-lg border px-3 py-1.5 text-sm ${tc.border} ${tc.textSecondary} ${tc.buttonHover}`}
          >
            <i className="ri-close-line mr-1" />
            {t("payroll.attendanceFilters.clearCustom")}
          </button>
        )}

        {/* Persona: solo dueños de Staff (#1143) */}
        {isStaffAdmin && (
          <label className="flex flex-col gap-1">
            <span className={`text-xs ${tc.textSecondary}`}>
              {t("payroll.attendanceFilters.person")}
            </span>
            <select
              aria-label={t("payroll.attendanceFilters.person")}
              value={teamMemberId}
              onChange={(e) => setTeamMemberId(e.target.value)}
              className={`${inputCls} min-w-[12rem]`}
            >
              <option value="">{t("payroll.attendanceFilters.everyone")}</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className={`mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs ${tc.textSecondary}`}>
        {viewSwitch}
        <span data-testid="attendance-range">
          <i className="ri-calendar-line mr-1" />
          {t("payroll.attendanceFilters.showing", {
            from: range.from || "…",
            to: range.to || "…",
          })}
        </span>
        {!isStaffAdmin && (
          <span>
            <i className="ri-lock-line mr-1" />
            {t("payroll.attendanceFilters.onlyYours")}
          </span>
        )}
      </div>

      {invalidRange && (
        <p role="alert" className="mt-2 text-sm text-red-500">
          {t("payroll.attendanceFilters.invalidRange")}
        </p>
      )}

      {totals && !invalidRange && (
        <dl data-testid="attendance-summary" className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {(
            [
              ["daysWorked", formatDays(totals.daysWorked)],
              ["absences", String(totals.absences)],
              ["hoursWorked", `${totals.hoursWorked} h`],
              ["overtime", `${totals.overtime} h`],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className={`rounded-xl border px-3 py-2 ${tc.border}`}>
              <dt className={`text-xs ${tc.textSecondary}`}>
                {t(`payroll.attendanceFilters.summary.${key}`)}
              </dt>
              <dd className={`text-lg font-semibold ${tc.text}`} data-testid={`summary-${key}`}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );

  const colorOf = (id: string) => people.find((p) => p.id === id)?.color ?? null;
  const heatmap =
    !loading && !invalidRange && view === "calendar" ? (
      <div className="mb-4">
        <AttendanceHeatmap
          records={rows}
          from={range.from}
          to={range.to}
          labelOf={(k) => (k === "late" ? t("staffOps.heatmap.late") : attendanceStatusLabel(t, k))}
          colorOf={colorOf}
          title={t("staffOps.heatmap.title")}
          emptyLabel={t("staffOps.heatmap.empty")}
          overtimeLabel={t("staffOps.heatmap.overtime")}
          truncatedLabel={t("staffOps.heatmap.truncated", { count: 62 })}
        />
      </div>
    ) : null;
  const badge = (tone: string, label: string) => (
    <span className={`rounded-md px-2 py-0.5 text-[11px] font-medium ${tone}`}>{label}</span>
  );

  const columns: Column[] = [
    { key: "date", label: t("payroll.fields.date"), width: "104px" },
    {
      key: "teamMemberName",
      label: t("payroll.fields.teamMember"),
      render: (row) => String(row.teamMemberName || row.teamMemberId || "—"),
    },
    {
      key: "status",
      label: t("payroll.fields.attendanceStatus"),
      render: (row) => {
        const status = String(row.status || "present");
        return badge(
          ATTENDANCE_STATUS_TONES[status] ?? "bg-gray-400/15 text-gray-500",
          attendanceStatusLabel(t, status),
        );
      },
    },
    {
      key: "days",
      label: t("payroll.fields.days"),
      align: "right",
      render: (row) => formatDays(row.days as number | null),
    },
    {
      key: "hoursWorked",
      label: t("payroll.fields.hoursWorked"),
      align: "right",
      // "—" y no 0: un marcaje ausente no es una jornada de cero horas.
      render: (row) => {
        const hours = row.hoursWorked as number | null | undefined;
        return hours === null || hours === undefined ? "—" : `${hours} h`;
      },
    },
    {
      key: "overtime",
      label: t("payroll.fields.overtime"),
      align: "right",
      render: (row) => {
        const extra = row.overtime as number | null | undefined;
        return extra === null || extra === undefined ? "—" : `${extra} h`;
      },
    },
    {
      key: "locationName",
      label: t("payroll.fields.location"),
      render: (row) => String(row.locationName || row.locationId || "—"),
    },
    {
      key: "source",
      label: t("payroll.fields.source"),
      render: (row) => {
        const source = String(row.source || "manual");
        return clockSourceLabel(t, source);
      },
    },
  ];

  const fields: FormField[] = [
    { key: "date", label: t("payroll.fields.date"), type: "date", required: true },
    { key: "teamMemberId", label: t("payroll.fields.teamMemberId"), required: true },
    { key: "teamMemberName", label: t("payroll.fields.teamMemberName") },
    {
      key: "status",
      label: t("payroll.fields.attendanceStatus"),
      type: "select",
      defaultValue: "present",
      options: ATTENDANCE_STATUS_OPTIONS.map((e) => ({
        value: e,
        label: attendanceStatusLabel(t, e),
      })),
    },
    {
      key: "checkIn",
      label: t("payroll.fields.checkIn"),
      placeholder: "08:00",
      hint: t("payroll.hints.hour"),
    },
    { key: "checkOut", label: t("payroll.fields.checkOut"), placeholder: "17:00" },
    {
      key: "days",
      label: t("payroll.fields.days"),
      type: "number",
      hint: t("payroll.hints.days"),
    },
    { key: "overtime", label: t("payroll.fields.overtime"), type: "number" },
    { key: "locationId", label: t("payroll.fields.locationId"), hint: t("payroll.hints.location") },
    { key: "locationName", label: t("payroll.fields.locationName") },
    { key: "projectId", label: t("payroll.fields.projectId") },
    { key: "costCenterId", label: t("payroll.fields.costCenterId") },
    { key: "notes", label: t("payroll.fields.notes"), type: "textarea" },
  ];

  const handleCreate = async (values: Record<string, string>) => {
    await createAttendance(toPayload(values));
    await load();
  };

  const handleUpdate = async (id: string, values: Record<string, string>) => {
    await updateAttendance(id, toPayload(values));
    await load();
  };

  const handleDelete = async (id: string) => {
    await deleteAttendance(id);
    await load();
  };

  return (
    <ApiCrudSection
      readOnly={!isStaffAdmin}
      title={t("payroll.menu.attendance")}
      subtitle={t("payroll.attendanceSubtitle")}
      icon="ri-calendar-check-line"
      banner={
        <>
          {filterBar}
          {heatmap}
        </>
      }
      addLabel={t("payroll.newAttendance")}
      columns={columns}
      fields={fields}
      rows={rows as unknown as Row[]}
      loading={loading}
      error={error}
      searchKey="teamMemberName"
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
    />
  );
}
